// The landing's live hero (2026-11-01, Devin: "a similar hero experience to the sheep tag where it shows a game in
// action in the background and you can join in and assume the positions of the bot"; Sheep Tag's
// src/landing/heroLive.ts). On a capable desktop the lid's loop gives way to the game itself running a bot match: the
// real /play page in a frame in its live mode (main.ts ?live=1: no HUD, no title, no sound, no stats, a calm camera),
// loaded only after the page is settled, kept only if it holds the frame rate. Phones, weak devices, reduced motion
// and Save-Data keep the loop (or the painting). The rules are here, pure, so tests/live-hero.test.ts holds them;
// landing-page.ts wires them.

/** Under this many frames a second the frame goes and the loop stays (Sheep Tag's number). */
export const LIVE_FPS = 38;
/** How long the frame rate is judged, after the frame has drawn its first second (shaders compiled, armies loaded). */
export const WARM_MS = 1500, JUDGE_MS = 3000;
/** From the frame's start to its match on screen; past it the loop stays (Sheep Tag's 14 s boot cap). */
export const BOOT_MS = 14_000;
/** The judge looks this often. */
export const JUDGE_TICK_MS = 250;
/** Running time since the judge last looked (0 after a hold): at most two looks' worth, so a throttled timer's gap
 *  (a background tab fires once a second, or a minute) is never counted as time the frame had and drew nothing in. */
export function judgeDt(at: number, now: number) { return at ? Math.min(JUDGE_TICK_MS * 2, Math.max(0, now - at)) : 0; }

/** The window the live hero asks for: a mouse or trackpad and room for it. */
export const LIVE_MEDIA = '(pointer: fine) and (hover: hover) and (min-width: 900px)';
export interface Device { desktop: boolean; cores: number; memory: number | undefined; webgl2: boolean }
/** A desktop that can run the game behind the page: LIVE_MEDIA, four cores, 4 GB where the browser says, WebGL2. */
export const liveCapable = (d: Device) => d.desktop && d.cores >= 4 && (d.memory === undefined || d.memory >= 4) && d.webgl2;

export interface LiveState {
  capable: boolean;
  loaded: boolean;
  /** html[data-open] is absent or "done" */
  openDone: boolean;
  onScreen: boolean;
  reduced: boolean;
  saveData: boolean;
  /** it was tried and given up (too slow, too long to start, or broken): never again this visit */
  gaveUp: boolean;
}
/** Whether the frame should exist now. Once it runs it stays while it scrolls away (the scene coordinator pauses it). */
export const liveWanted = (s: LiveState) => s.capable && s.loaded && s.openDone && s.onScreen && !s.reduced && !s.saveData && !s.gaveUp;

/** The verdict on `frames` drawn over `ms` of running time since the warm-up (time paused or hidden never counts). */
export function judgeFps(frames: number, ms: number): 'wait' | 'keep' | 'give-up' {
  if (ms < JUDGE_MS) return 'wait';
  return (frames * 1000) / ms >= LIVE_FPS ? 'keep' : 'give-up';
}

/** The landing's judge of the frame, fed a look every JUDGE_TICK_MS while the frame runs. Time and frames are counted
 *  over the same looks only: the look that spans a hold (reset(), as it pauses or wakes) counts neither, so a pause
 *  never reads as slow frames nor a wake's frames as free ones (Flash round 4 F4). Booting (not ready) counts against
 *  BOOT_MS, then WARM_MS of running, then the rate over the looks after it. */
export function fpsJudge() {
  let at = 0, seen = -1, booting = 0, ran = 0, frames = 0, ms = 0;
  return {
    reset() { at = 0; seen = -1; },
    look(now: number, st: { ready: boolean; frames: number }): 'wait' | 'keep' | 'give-up' | 'slow-start' {
      const dt = judgeDt(at, now), df = seen < 0 ? 0 : st.frames - seen;
      at = now; seen = st.frames;
      if (!st.ready) { booting += dt; return booting > BOOT_MS ? 'slow-start' : 'wait'; }
      const warm = ran >= WARM_MS;
      ran += dt;
      if (!warm || !dt) return 'wait';
      frames += df; ms += dt;
      return judgeFps(frames, ms);
    },
    fps: () => (ms ? Math.round((frames * 1000) / ms) : 0),
  };
}

/** The address of the frame: the game in its live mode, on a seed of its own, in the hero's sky, aimed (aimOf). */
export const liveSrc = (seed: number, sky: Record<string, string>, aim?: Aim) =>
  `/play/?${new URLSearchParams({ live: '1', seed: String(seed), ...sky, ...(aim ? { aim: aimParam(aim) } : {}) })}`;

// The aim (2026-11-02, launch merge; tinwhistlegames 1c176083: "the logo sits right on top of the center-tower fight"):
// the director films the fight in the middle of the frame, which is where the logo is. The landing measures the lid
// and hands the frame the middle of the open ground beside or under the logo, clear of the copy and the buttons; the
// game's camera (main.ts aimShift) then slides so the fight lands there. Fractions of the frame, 0 to 1 from top left.
export type Aim = [number, number];
export interface Rect { left: number; top: number; right: number; bottom: number }
/** A zone narrower or shorter than this (px) is no place for a fight. */
export const AIM_MIN = 160;
/** The middle of the largest open zone of the lid: right of the logo, left of it, or under it down to the copy, each
 *  above the copy and the buttons (`foot`). Ties go right, then under (the hand-off's order). Null when none is
 *  AIM_MIN both ways: the frame keeps the fight in the middle. */
export function aimOf(lid: Rect, logo: Rect, foot: Rect[]): Aim | null {
  const footTop = Math.min(lid.bottom, ...foot.map((r) => r.top));
  const zones: Rect[] = [
    { left: logo.right, top: lid.top, right: lid.right, bottom: footTop },
    { left: logo.left, top: logo.bottom, right: logo.right, bottom: footTop },
    { left: lid.left, top: lid.top, right: logo.left, bottom: footTop },
  ];
  let best: Rect | null = null, area = 0;
  for (const z of zones) {
    const w = z.right - z.left, h = z.bottom - z.top;
    if (w < AIM_MIN || h < AIM_MIN || w * h <= area) continue;
    best = z; area = w * h;
  }
  if (!best) return null;
  const W = lid.right - lid.left, H = lid.bottom - lid.top;
  return [+(((best.left + best.right) / 2 - lid.left) / W).toFixed(3), +(((best.top + best.bottom) / 2 - lid.top) / H).toFixed(3)];
}
export const aimParam = ([u, v]: Aim) => `${u},${v}`;
/** ?aim=u,v read back: two numbers, each kept inside 0.1 to 0.9 so the fight never sits on the frame's edge; else null. */
export function parseAim(q: string | null): Aim | null {
  const m = /^(-?\d*\.?\d+),(-?\d*\.?\d+)$/.exec(q ?? '');
  if (!m) return null;
  const k = (x: string) => Math.min(0.9, Math.max(0.1, Number(x)));
  return [k(m[1]), k(m[2])];
}

/** What the frame's page answers (main.ts window.__live). */
export interface LiveApi {
  state(): { ready: boolean; frames: number; seed: number; tick: number; seat: number | null; hash: string; cam: [number, number, number]; armies: string[] };
  pause(on: boolean): void;
  /** The point of the frame the fight goes to (aimOf), as the lid is now (it is read again on a resize). */
  aim(a: Aim | null): void;
  /** Where its soldiers in a fight are on screen, as fractions of the frame (scripts/hero-grabs.ts checks the aim). */
  fighters(): Aim[];
}
/** The frame's window.__live, kept only when it is this build's whole API. An open landing can load an older
 *  /play/main.js (a rollback) with no aim(), and the first call would throw (Astra F1, 2026-11-02). */
export function liveApiOf(x: unknown): LiveApi | null {
  const a = x as Partial<Record<keyof LiveApi, unknown>> | null | undefined;
  return a && (['state', 'pause', 'aim', 'fighters'] as const).every((k) => typeof a[k] === 'function') ? (a as LiveApi) : null;
}
